import React, { useState } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import { withDropdownFont } from "components/reactSelect/dropdownFontStyles";

const SearchableSelect = ({
  className,
  classNamePrefix,
  name,
  options,
  isMulti,
  isDisabled = false,
  isLoading = false,
  isClearable = true,
  isRtl = false,
  isSearchable = true,
  fontSize,
  width = "100%",
  defaultValue = null,
  onChange,
  onInputChange
}) => {
  const [inputValue, setInputValue] = useState("");
  const controlFontSize = "0.875rem";

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (onInputChange) {
      onInputChange(newValue);
    }
  };

  return (
    <Select
      className={className}
      classNamePrefix={classNamePrefix}
      closeMenuOnSelect={false}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isClearable={isClearable}
      isRtl={isRtl}
      isSearchable={isSearchable}
      name={name}
      options={options}
      isMulti={isMulti}
      menuPortalTarget={document.body}
      defaultValue={defaultValue} // Pass defaultValue here
      onChange={onChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      styles={{
        container: (provided) => withDropdownFont({
          ...provided,
          width,
          minHeight: "2.5rem",
          fontSize: controlFontSize,
        }),
        menu: (provided) => withDropdownFont({
          ...provided,
          width,
          fontSize: controlFontSize,
          borderRadius: 8,
          overflow: "hidden",
          boxShadow: "0 12px 32px rgba(20, 20, 43, 0.16)",
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999, // Ensure dropdown stays on top of other elements
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: "0 8px",
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
        control: (provided, state) => withDropdownFont({
          ...provided,
          minHeight: "2.5rem",
          borderRadius: 8,
          fontSize: controlFontSize,
          borderColor: state.isFocused ? "#35d1f5" : "#d2d6da",
          boxShadow: state.isFocused ? "0 0 0 2px rgba(33, 212, 253, 0.18)" : "none",
          "&:hover": {
            borderColor: state.isFocused ? "#35d1f5" : "#aab2bd",
          },
        }),
        valueContainer: (provided) => withDropdownFont({
          ...provided,
          minHeight: "2.375rem",
          padding: "0 12px",
          fontSize: controlFontSize,
        }),
        singleValue: (provided) => withDropdownFont({
          ...provided,
          fontSize: controlFontSize,
          color: "#344767",
        }),
        input: (provided) => withDropdownFont({
          ...provided,
          margin: 0,
          padding: 0,
          fontSize: controlFontSize,
        }),
        placeholder: (provided) => withDropdownFont({
          ...provided,
          color: "#8392ab",
          fontSize: controlFontSize,
        }),
        option: (provided, state) => withDropdownFont({
          ...provided,
          fontSize: controlFontSize,
          padding: "8px 12px",
          backgroundColor: state.isFocused ? "#f8f9fa" : "#ffffff",
          color: "#344767",
        }),
      }}
    />
  );
};

// Prop-Types validation
SearchableSelect.propTypes = {
  className: PropTypes.string.isRequired,
  classNamePrefix: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  isMulti: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isClearable: PropTypes.bool,
  isRtl: PropTypes.bool,
  isSearchable: PropTypes.bool,
  fontSize: PropTypes.string,
  width: PropTypes.string,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  defaultValue: PropTypes.oneOfType([
    PropTypes.object, // Single default value for non-multi-select
    PropTypes.array,  // Array of default values for multi-select
  ]),
};

export default SearchableSelect;
